/* =============================================================================
   Clinic selector — assets/clinic-selector.js

   Drives <clinic-selector> in the cart drawer. Searches the clinics API as the
   customer types, then writes the chosen clinic to every matching line item as
   two properties: a hidden ID and a readable name.

   Two parts are fiddlier than they look:

   1. Line item properties can only be changed through /cart/change.js, one line
      at a time, and every change issues a new line key. So the writer re-reads
      the cart before each change rather than caching keys up front.

   2. The theme re-renders the drawer whenever the cart changes. Replacing the
      whole section would destroy this element and visually close the drawer, so
      the refresh here mirrors the theme's own approach and swaps only
      <cart-drawer-items> and .cart-drawer__footer.
   ========================================================================== */

(function () {
  "use strict";

  if (!window.customElements || customElements.get("clinic-selector")) return;

  const DEBOUNCE_MS = 250;
  const SYNC_DEBOUNCE_MS = 250;
  const MAX_CART_WRITES = 60;

  /* Event fired by assets/b2b-med-spa-contact-form.js on a successful submit.
     It bubbles to document, so nothing in that file has to change. Registering
     from inside the drawer does not go through that form, but a customer may
     still fill in the full section on the page behind it — in which case the
     list here is stale and has to be refetched. */
  const FORM_SUCCESS_EVENT = "b2b-med-spa-contact-form:success";

  /* A clinic created a moment ago is not always queryable a moment later —
     indexing and approval both take time. Refetch straight away, then twice
     more, before admitting the new clinic is not there yet. Only reached when
     the create call did not hand back the new clinic itself. */
  const REFETCH_DELAYS_MS = [0, 2000, 5000];

  /* Byte-for-byte the section's rule, so an address the full form accepts is
     never rejected here and vice versa. Requires a real alphabetic TLD, which
     the looser "anything after the dot" version this replaced did not. */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

  /* Also the section's list, verbatim. Only consulted when the business-email
     rule is switched on. */
  const FREE_EMAIL_DOMAINS = [
    "gmail.com",
    "googlemail.com",
    "yahoo.com",
    "yahoo.co.uk",
    "yahoo.co.in",
    "ymail.com",
    "rocketmail.com",
    "hotmail.com",
    "hotmail.co.uk",
    "outlook.com",
    "live.com",
    "msn.com",
    "aol.com",
    "icloud.com",
    "me.com",
    "mac.com",
    "gmx.com",
    "gmx.net",
    "mail.com",
    "zoho.com",
    "yandex.com",
    "yandex.ru",
    "protonmail.com",
    "proton.me",
    "pm.me",
    "tutanota.com",
    "hushmail.com",
    "comcast.net",
    "verizon.net",
    "att.net",
    "sbcglobal.net",
    "bellsouth.net",
    "cox.net",
    "charter.net",
    "earthlink.net",
    "juno.com",
    "optonline.net",
    "mailinator.com",
    "guerrillamail.com",
    "yopmail.com",
    "sharklasers.com",
    "10minutemail.com",
    "temp-mail.org",
    "tempmail.com",
    "trashmail.com",
    "throwawaymail.com",
    "getnada.com",
    "dispostable.com",
    "maildrop.cc",
    "fakeinbox.com",
    "mailnesia.com",
    "moakt.com",
    "emailondeck.com",
  ];

  /* Digs the first human-readable string out of whatever an endpoint calls an
     error: a plain string, a list, or an object keyed by field. */
  function firstMessage(value) {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    if (Array.isArray(value)) return firstMessage(value[0]);

    if (typeof value === "object") {
      const key = Object.keys(value)[0];
      return key ? firstMessage(value[key]) : "";
    }

    return "";
  }

  function isFreeEmail(value) {
    const domain = String(value).split("@")[1];
    if (!domain) return false;
    return FREE_EMAIL_DOMAINS.indexOf(domain.toLowerCase().trim()) !== -1;
  }

  /* Mirrors the order of the rows in the snippet. It decides which empty field
     gets the focus and the first error, so it has to match what is on screen —
     clinic name and state come last, as a pair. */
  const NEW_CLINIC_FIELDS = ["firstName", "lastName", "email", "phone", "name", "state"];

  /* Field key -> the key sent to the API. These are the B2B section's own field
     labels, because the dialog posts to the same endpoint the section posts to
     and that endpoint is keyed by label, not by any tidier internal name. Every
     one is overridable from theme settings, since the labels are themselves
     theme settings on the section's blocks. */
  const DEFAULT_API_KEYS = {
    name: "Practice / Clinic Name",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Mobile Phone",
    state: "State",
  };

  /* Shared with assets/b2b-med-spa-contact-form.js — same key, same object
     shape. Attribution is captured once per session and read by whichever form
     the customer ends up using, so a referral code survives the walk from a
     ?ref= landing page to the cart. */
  const TOUCH_KEY = "b2bcf_first_touch";

  /* The section's eight preset hidden blocks, in its order. Each line is
     "source | key sent to the API" — the left side is the section's own source
     name, the right side is whatever the clinics endpoint wants to call it. */
  const DEFAULT_TRACKING_MAP =
    "ref | Referral Code\n" +
    "utm_source | UTM Source\n" +
    "utm_medium | UTM Medium\n" +
    "utm_campaign | UTM Campaign\n" +
    "landing_page | Landing Page\n" +
    "referrer | Referrer\n" +
    "device | Device\n" +
    "browser | Browser";

  /* Every empty tracking value is sent as "(none)" rather than omitted, so a
     lead from the drawer has the same shape as one from the section — a
     missing key and an explicitly empty one read very differently downstream. */
  const TRACKING_FALLBACK = "(none)";

  function queryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name) || "";
    } catch (error) {
      return "";
    }
  }

  function detectBrowser() {
    const ua = navigator.userAgent || "";
    if (/Edg\//.test(ua)) return "Edge";
    if (/OPR\/|Opera/.test(ua)) return "Opera";
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Safari\//.test(ua)) return "Safari";
    if (/MSIE|Trident/.test(ua)) return "Internet Explorer";
    return "Other";
  }

  function detectDevice() {
    const ua = navigator.userAgent || "";

    let os = "Unknown";
    if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
    else if (/Android/.test(ua)) os = "Android";
    else if (/Mac OS X/.test(ua)) os = "macOS";
    else if (/Windows/.test(ua)) os = "Windows";
    else if (/Linux/.test(ua)) os = "Linux";

    let type = "Desktop";
    if (/Mobi|iPhone|iPod|Android.*Mobile/.test(ua)) type = "Mobile";
    else if (/iPad|Tablet|Android/.test(ua)) type = "Tablet";

    return `${type} / ${os}`;
  }

  /* Writes only when nothing is stored yet, exactly as the section does — so
     whichever script runs first on the landing page wins, and the other reads
     what it wrote. The drawer is on every page, which means this often captures
     attribution on visits that never reach the contact section at all. */
  function firstTouch() {
    let stored = null;

    try {
      stored = JSON.parse(window.sessionStorage.getItem(TOUCH_KEY) || "null");
    } catch (error) {
      stored = null;
    }

    if (stored && typeof stored === "object") return stored;

    const data = {
      utm_source: queryParam("utm_source"),
      utm_medium: queryParam("utm_medium"),
      utm_campaign: queryParam("utm_campaign"),
      utm_term: queryParam("utm_term"),
      utm_content: queryParam("utm_content"),
      gclid: queryParam("gclid"),
      fbclid: queryParam("fbclid"),
      ref: queryParam("ref"),
      landing_page: window.location.href,
      landing_path: window.location.pathname + window.location.search,
      referrer: document.referrer || "",
    };

    try {
      window.sessionStorage.setItem(TOUCH_KEY, JSON.stringify(data));
    } catch (error) {
      /* private mode — fall through with in-memory values */
    }

    return data;
  }

  /* A live URL parameter beats the stored one: a customer who arrives on a
     second, differently tagged link should be credited to it. */
  function trackingValue(key) {
    const touch = firstTouch();

    switch (key) {
      case "landing_page":
        return touch.landing_page || window.location.href;
      case "landing_path":
        return touch.landing_path || window.location.pathname + window.location.search;
      case "referrer":
        return touch.referrer || document.referrer || "";
      case "page_url":
        return window.location.href;
      case "page_title":
        return document.title || "";
      case "device":
        return detectDevice();
      case "browser":
        return detectBrowser();
      case "timestamp":
        return new Date().toISOString();
      default:
        return queryParam(key) || touch[key] || "";
    }
  }

  function root() {
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return base.replace(/\/$/, "");
  }

  function cartUrl() {
    if (window.routes && window.routes.cart_url) return window.routes.cart_url;
    return root() + "/cart";
  }

  function parseHeaders(text) {
    const headers = {};
    if (!text) return headers;

    text.split(/\r?\n/).forEach((line) => {
      const idx = line.indexOf(":");
      if (idx < 1) return;
      const key = line.slice(0, idx).trim();
      if (key) headers[key] = line.slice(idx + 1).trim();
    });

    return headers;
  }

  /* Survives the element itself. Saving a clinic refreshes the drawer footer,
     and this snippet renders inside that footer — so the element that started
     the save is detached before the save finishes, taking any message on it
     with it. The note is parked here and picked up by whichever element is
     actually on screen afterwards. */
  let pendingNotice = "";

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  class ClinicSelector extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const data = this.dataset;

      this.apiBase = (data.apiBase || "").replace(/\/$/, "");
      this.apiHeaders = parseHeaders(data.apiHeaders);
      this.preloadAll = data.preload !== "false";
      this.sendLimit = data.sendLimit !== "false";
      this.limit = parseInt(data.limit || "20", 10);

      /* Two independent switches.

         preloadAll picks the mode:
           off — the endpoint searches; the field is a dropdown that queries on
                 each keystroke.
           on  — one request on load returns everything; it is shown as a
                 permanently visible list and typing filters it here.

         sendQueryParam decides only whether q appears in the URL. It stays
         meaningful while preloading because plenty of endpoints return nothing
         for a missing q but everything for an empty one — so preload + q on
         asks for "?q=", and preload + q off asks for the bare collection. */
      this.sendQueryParam = data.sendQuery !== "false";
      this.serverSearch = !this.preloadAll;
      this.inline = !this.serverSearch;
      this.minChars = this.serverSearch ? parseInt(data.minChars || "2", 10) : 0;
      this.cache = null;

      if (this.inline) this.classList.add("is-inline");
      this.propId = data.propId || "_clinic_id";
      this.propName = data.propName || "Clinic";
      this.required = data.required === "true";
      this.debug = data.debug === "true";

      this.productIds = (data.productIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      this.text = {
        noResults: data.textNoResults || "No clinics found.",
        error: data.textError || "Could not load clinics. Please try again.",
        required: data.textRequired || "Choose a clinic to continue to checkout.",
        pending: data.textPending || "Your clinic has been submitted and will appear here shortly.",
        createError: data.textCreateError || "We could not add your clinic. Please try again.",
        createRequired: data.textCreateRequired || "This field is required.",
        createEmail: data.textCreateEmail || "Please enter a valid email address.",
        createBusinessEmail: data.textCreateBusinessEmail || "Please use your work email address.",
        createSuccess: data.textCreateSuccess || "Clinic added. You can continue to checkout.",
      };

      /* Which key of the submitted form payload holds the clinic name. The
         payload is keyed by each field block's "Name" setting, so this mirrors
         a theme setting rather than being hard-coded. */
      this.formFieldName = data.formField || "";

      /* The section's contact endpoint, which lives on the same service as the
         clinic search — /api/public/contact alongside /api/public/clinics — so
         this is a path on the same base by default. A full URL is accepted too,
         for a setup where the two are split. Headers likewise default to the
         search API's, since one service normally means one set of headers. */
      this.createPath = data.createPath || "/api/public/contact";
      this.createHeaders = parseHeaders(data.createHeaders);
      if (!Object.keys(this.createHeaders).length) this.createHeaders = this.apiHeaders;

      this.createWrapKey = data.createWrapKey || "";
      this.sendEmail = data.createSendEmail !== "false";
      this.businessEmail = data.businessEmail === "true";

      this.apiKeys = {};
      NEW_CLINIC_FIELDS.forEach((key) => {
        this.apiKeys[key] = data[`key${key.charAt(0).toUpperCase()}${key.slice(1)}`] || DEFAULT_API_KEYS[key];
      });

      /* "source | api key" per line, same idea as the section's email field map.
         An entry with no pipe sends the source under its own name. */
      this.trackingMap = (data.trackingMap || DEFAULT_TRACKING_MAP)
        .split(/\r?\n/)
        .map((line) => {
          const parts = line.split("|");
          const source = (parts[0] || "").trim();
          const key = (parts[1] || parts[0] || "").trim();
          return source && key ? { source, key } : null;
        })
        .filter(Boolean);

      /* Capture attribution now, not at submit time. The drawer is on every
         page, so this pins the referral code on the landing visit even when
         the customer never opens the contact section — and by the time they
         reach the cart the ?ref= is usually long gone from the URL. */
      firstTouch();

      this.selected = data.selectedId ? { id: data.selectedId, label: data.selectedName } : null;

      this.input = this.querySelector("[data-clinic-input]");
      this.results = this.querySelector("[data-clinic-results]");
      this.spinner = this.querySelector("[data-clinic-spinner]");
      this.message = this.querySelector("[data-clinic-message]");
      this.searchPane = this.querySelector("[data-clinic-search]");
      this.chosenPane = this.querySelector("[data-clinic-chosen]");
      this.chosenName = this.querySelector("[data-clinic-chosen-name]");
      this.changeButton = this.querySelector("[data-clinic-change]");

      this.addButton = this.querySelector("[data-clinic-add]");
      this.modal = this.querySelector("[data-clinic-modal]");
      this.modalError = this.querySelector("[data-clinic-modal-error]");
      this.createForm = this.querySelector("[data-clinic-form]");
      this.createButton = this.querySelector("[data-clinic-create]");

      this.fields = {};
      NEW_CLINIC_FIELDS.forEach((key) => {
        const field = this.querySelector(`[data-clinic-field="${key}"]`);
        if (field) this.fields[key] = field;
      });

      this.items = [];
      this.activeIndex = -1;
      this.busy = false;

      this.bindEvents();
      this.watchCart();
      this.updateCheckoutState();

      /* syncFromCart settles the chosen/search panes first — preloading before it
         would only have the list wiped when the search pane is (re)shown. */
      this.syncFromCart().then(() => {
        if (this.inline) this.search("");
        this.flushNotice();
      });
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.onDocumentClick);
      document.removeEventListener("submit", this.onFormSubmit, true);
      document.removeEventListener(FORM_SUCCESS_EVENT, this.onFormSuccess);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.syncTimer);
      clearTimeout(this.refetchTimer);

      /* Saving a clinic refreshes the drawer footer and replaces this element.
         Its dialog is parked on <body> by then, outside the part being swapped,
         so it has to be taken away by hand — the replacement brings its own. */
      if (this.modal && this.modal.parentNode === document.body) {
        document.body.removeChild(this.modal);
      }
    }

    /* ---------------------------------------------------------------- events */

    bindEvents() {
      if (this.input) {
        this.input.addEventListener("input", () => this.onType());
        this.input.addEventListener("keydown", (event) => this.onKeyDown(event));
        this.input.addEventListener("focus", () => {
          if (this.items.length) {
            this.openList();
            return;
          }

          /* Nothing to type before results exist when the whole list is loaded. */
          if (this.minChars === 0) this.search(this.input.value.trim());
        });
      }

      if (this.changeButton) {
        this.changeButton.addEventListener("click", () => this.showSearch());
      }

      if (this.addButton) {
        this.addButton.addEventListener("click", () => this.openModal());
      }

      if (this.modal) {
        this.modal.addEventListener("click", (event) => {
          if (!event.target.closest("[data-clinic-modal-close]")) return;

          /* A click on the backdrop while the request is in flight is almost
             always a misclick, and acting on it would throw away a form the
             customer has just filled in for a submission that may still
             succeed. The X and Cancel still work — this only ignores the
             backdrop, which is the easy one to hit by accident. */
          if (this.busy && event.target.closest(".clinic-selector__modal-overlay")) return;

          this.closeModal();
        });

        /* Enter submits and Escape closes, the way a real dialog would. Escape
           is stopped from travelling any further because the drawer reads it as
           "close me", which would take the customer out of the very flow this
           dialog exists to keep them in — and the drawer listens on keyup, so
           swallowing keydown alone is not enough. */
        this.modal.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            this.closeModal();
            return;
          }

          if (event.key === "Enter" && event.target.matches("[data-clinic-field]")) {
            event.preventDefault();
            this.createClinic();
          }
        });

        this.modal.addEventListener("keyup", (event) => {
          if (event.key === "Escape") event.stopPropagation();
        });

        NEW_CLINIC_FIELDS.forEach((key) => {
          const field = this.fields[key];
          if (!field) return;

          /* Typing clears the error rather than re-running the check — nobody
             wants to be told their address is invalid while still typing it. */
          field.addEventListener("input", () => this.markField(key, true));
        });

        /* The address is checked on the way out of the field, as the section
           does it, so a typo surfaces next to the field instead of at submit. */
        if (this.fields.email) {
          this.fields.email.addEventListener("blur", () => {
            const value = this.fields.email.value.trim();
            if (!value) return;

            const problem = this.emailProblem(value);
            this.markField("email", !problem, problem);
          });
        }
      }

      if (this.createButton) {
        this.createButton.addEventListener("click", () => this.createClinic());
      }

      if (this.results) {
        /* mousedown rather than click — the input's blur closes the list first */
        this.results.addEventListener("mousedown", (event) => {
          const option = event.target.closest("[data-clinic-option]");
          if (!option) return;
          event.preventDefault();
          this.choose(this.items[parseInt(option.dataset.index, 10)]);
        });
      }

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeList();
      };

      document.addEventListener("click", this.onDocumentClick);

      /* Backstop for the disabled checkout button: a form can still be submitted
         by pressing Enter in a field, and the theme replaces the drawer's <form>
         on every cart update — so the listener sits on document, in the capture
         phase, where it survives those swaps and runs before the theme's own. */
      this.onFormSubmit = (event) => {
        const form = event.target;
        if (!form || !form.closest || !form.closest("cart-drawer")) return;
        if (!this.required || this.hidden || this.selected) return;

        event.preventDefault();
        event.stopPropagation();

        this.showMessage(this.text.required, "note");
        if (this.input) this.input.focus();
      };

      document.addEventListener("submit", this.onFormSubmit, true);

      /* On document rather than on the form: the B2B section can be re-rendered
         by the theme editor, and this element outlives any single form node. */
      this.onFormSuccess = (event) => this.onClinicSubmitted(event);
      document.addEventListener(FORM_SUCCESS_EVENT, this.onFormSuccess);
    }

    onType() {
      const query = this.input.value.trim();

      clearTimeout(this.timer);

      if (query.length < this.minChars) {
        this.abort();
        this.items = [];
        this.closeList();
        this.setSpinner(false);
        return;
      }

      /* Debouncing only earns its keep against the network. Once the list is
         cached locally, filtering on every keystroke is instant. */
      const delay = !this.serverSearch && this.cache ? 0 : DEBOUNCE_MS;
      this.timer = setTimeout(() => this.search(query), delay);
    }

    onKeyDown(event) {
      if (event.key === "Escape") {
        this.closeList();
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (!this.items.length) return;
        event.preventDefault();

        let next = this.activeIndex + (event.key === "ArrowDown" ? 1 : -1);
        if (next < 0) next = this.items.length - 1;
        if (next >= this.items.length) next = 0;

        this.setActive(next);
        return;
      }

      if (event.key === "Enter" && this.activeIndex > -1) {
        event.preventDefault();
        this.choose(this.items[this.activeIndex]);
      }
    }

    /* ---------------------------------------------------------------- search */

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    /* Either parameter can be switched off in theme settings for endpoints that
       cannot search, or that reject anything they do not recognise. With both
       off the request is a bare GET on the collection. */
    buildUrl(query) {
      const params = new URLSearchParams();
      if (this.sendQueryParam) params.set("q", query);
      if (this.sendLimit) params.set("limit", String(this.limit));

      const qs = params.toString();
      return `${this.apiBase}/api/public/clinics${qs ? `?${qs}` : ""}`;
    }

    /* Only used when the search term is not sent: the endpoint returns
       everything, so the matching happens here. */
    filterLocally(list, query) {
      if (!query) return list.slice(0, this.limit);

      const needle = query.toLowerCase();

      return list
        .filter((clinic) => {
          const name = String(clinic.name || "").toLowerCase();
          const city = String(clinic.city || "").toLowerCase();
          return name.indexOf(needle) !== -1 || city.indexOf(needle) !== -1;
        })
        .slice(0, this.limit);
    }

    /* Returns a promise that settles when the results on screen are up to date,
       so callers that need to inspect them (the post-submit refetch) can wait.
       Every early return resolves rather than returning undefined. */
    search(query) {
      if (!this.apiBase) {
        this.showMessage(this.text.error);
        /* eslint-disable-next-line no-console */
        console.error("[clinic-selector] no API base URL set in theme settings");
        return Promise.resolve();
      }

      /* The full list only has to be fetched once — after that, typing filters
         what is already in memory and never touches the network again. */
      if (!this.serverSearch) {
        if (this.cache) {
          this.items = this.filterLocally(this.cache, query);
          this.renderResults();
          return Promise.resolve();
        }

        /* Preload, the cart sync and a focus can all land in the same tick, and
           one list is enough. Whichever request is already running will render
           against whatever has been typed by the time it lands. */
        if (this.loading) return Promise.resolve();
        this.loading = true;
      }

      this.abort();
      this.controller = typeof AbortController === "function" ? new AbortController() : null;
      this.setSpinner(true);
      this.hideMessage();

      const url = this.buildUrl(query);

      return fetch(url, {
        method: "GET",
        headers: this.apiHeaders,
        signal: this.controller ? this.controller.signal : undefined,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((payload) => {
          /* Accept a bare array, or a wrapper such as { clinics: [...] } */
          const list = Array.isArray(payload)
            ? payload
            : (payload && (payload.clinics || payload.data || payload.results)) || [];

          const clinics = list.filter((entry) => entry && entry.id);

          if (this.serverSearch) {
            this.items = clinics.slice(0, this.limit);
          } else {
            this.loading = false;

            /* Only a full list is ever cached, and only when it has something
               in it. Two ways this used to go wrong, both of which stopped the
               selector searching at all:

                 An endpoint that returns nothing for an empty q cached [],
                 which is truthy — so every later keystroke filtered an empty
                 array and no request was ever made again.

                 A response to a narrowed query cached that subset, so a broader
                 search afterwards could only ever match within it.

               Leaving the cache null in both cases turns the next keystroke
               into a real request carrying ?q=, which is exactly what an
               endpoint that insists on a search term wants. */
            if (!query && clinics.length) this.cache = clinics;

            if (!query && !clinics.length) {
              /* eslint-disable-next-line no-console */
              console.warn(
                "[clinic-selector] the endpoint returned no clinics for " +
                  url +
                  "\nFalling back to querying it on each keystroke, which works if the" +
                  "\nendpoint needs a non-empty q. To skip this wasted first request," +
                  '\nturn off "Load all clinics on page load" in theme settings.',
              );
            }

            /* Filter against what is in the field now, not what was there when
               the request left — the customer may have typed while it flew.
               With no cache the endpoint has already done the filtering. */
            const current = this.input ? this.input.value.trim() : query;
            this.items = this.cache
              ? this.filterLocally(this.cache, current)
              : clinics.slice(0, this.limit);
          }

          if (this.debug) {
            /* eslint-disable-next-line no-console */
            console.log(`[clinic-selector] ${url}`, {
              returned: clinics.length,
              shown: this.items.length,
              filteredInBrowser: !this.serverSearch,
              mode: this.serverSearch ? "server search" : "preloaded list",
            });
          }

          this.setSpinner(false);
          this.renderResults();
        })
        .catch((error) => {
          this.loading = false;
          if (error && error.name === "AbortError") return;
          this.setSpinner(false);
          this.items = [];
          this.closeList();
          this.showMessage(this.text.error);
          /* eslint-disable-next-line no-console */
          console.error("[clinic-selector] search failed", error, url);
        });
    }

    labelFor(clinic) {
      if (!clinic) return "";
      return clinic.city ? `${clinic.name} — ${clinic.city}` : clinic.name;
    }

    renderResults() {
      if (!this.items.length) {
        this.results.innerHTML = `<li class="clinic-selector__empty">${escapeHtml(this.text.noResults)}</li>`;
        this.openList();
        this.activeIndex = -1;
        return;
      }

      this.results.innerHTML = this.items
        .map((clinic, index) => {
          const city = clinic.city
            ? `<span class="clinic-selector__option-city">${escapeHtml(clinic.city)}</span>`
            : "";

          return (
            `<li class="clinic-selector__option" role="option" aria-selected="false" ` +
            `id="ClinicSelector-Option-${index}" data-clinic-option data-index="${index}">` +
            `<span class="clinic-selector__option-name">${escapeHtml(clinic.name)}</span>${city}</li>`
          );
        })
        .join("");

      this.openList();
      this.setActive(0);
    }

    setActive(index) {
      const options = this.results.querySelectorAll("[data-clinic-option]");

      options.forEach((option, i) => {
        const isActive = i === index;
        option.classList.toggle("is-active", isActive);
        option.setAttribute("aria-selected", isActive ? "true" : "false");
        if (isActive && option.scrollIntoView) option.scrollIntoView({ block: "nearest" });
      });

      this.activeIndex = index;

      if (this.input) {
        if (index > -1) {
          this.input.setAttribute("aria-activedescendant", `ClinicSelector-Option-${index}`);
        } else {
          this.input.removeAttribute("aria-activedescendant");
        }
      }
    }

    openList() {
      this.results.hidden = false;
      if (this.input) this.input.setAttribute("aria-expanded", "true");
    }

    closeList() {
      /* An inline list is the UI, not an overlay — clicking away or pressing
         Escape must not make the clinics disappear. */
      if (this.inline) {
        this.activeIndex = -1;
        return;
      }

      this.results.hidden = true;
      this.activeIndex = -1;
      if (this.input) {
        this.input.setAttribute("aria-expanded", "false");
        this.input.removeAttribute("aria-activedescendant");
      }
    }

    /* -------------------------------------------------- after a form submit */

    /* A clinic was registered through the full B2B med spa form on the page,
       rather than through the dialog here — so the list is stale and gets
       refetched, with the submitted name typed in ready to confirm. */
    onClinicSubmitted(event) {
      const detail = (event && event.detail) || {};
      const submitted = this.clinicNameFromPayload(detail.payload);

      if (this.debug) {
        /* eslint-disable-next-line no-console */
        console.log("[clinic-selector] form submitted elsewhere on the page", { clinic: submitted });
      }

      /* Nothing in the cart needs a clinic, so there is no list worth refreshing. */
      if (this.hidden) return;

      this.refetchAfterSubmit(submitted, 0);
    }

    /* The payload is keyed by each field block's "Name" setting. The configured
       key is tried first; the scan is a fallback for a renamed field, and looks
       for "clinic" before "practice" so it cannot land on "Practice Type". */
    clinicNameFromPayload(payload) {
      if (!payload) return "";

      const configured = this.formFieldName && payload[this.formFieldName];
      if (configured) return String(configured).trim();

      const keys = Object.keys(payload);
      const find = (needle) =>
        keys.find((key) => key.toLowerCase().indexOf(needle) !== -1 && String(payload[key] || "").trim());

      const key = find("clinic") || find("practice name");
      return key ? String(payload[key]).trim() : "";
    }

    /* Refetch, then check whether the submitted clinic actually came back. If
       it did not, try again on the remaining schedule before showing the note —
       a clinic created seconds ago is often not queryable yet. */
    refetchAfterSubmit(name, attempt) {
      const delay = REFETCH_DELAYS_MS[attempt];

      if (delay === undefined) {
        if (name) this.showMessage(this.text.pending, "note");
        return;
      }

      clearTimeout(this.refetchTimer);

      this.refetchTimer = setTimeout(() => {
        this.reloadClinics(name)
          .then(() => {
            if (!name || this.knowsClinic(name)) return;
            this.refetchAfterSubmit(name, attempt + 1);
          })
          .catch(() => this.refetchAfterSubmit(name, attempt + 1));
      }, delay);
    }

    /* Drops the cached list so the next search is a real request, prefills the
       field with the submitted name, and re-runs the search.

       In preloaded mode the request stays a bare "return everything" call and
       the name is applied as a local filter — sending it as q would cache a
       filtered subset and quietly break every later keystroke. */
    reloadClinics(name) {
      this.cache = null;
      this.loading = false;

      if (name && this.input) this.input.value = name;

      const term = this.serverSearch ? name || "" : "";
      return Promise.resolve(this.search(term));
    }

    knowsClinic(name) {
      if (!name) return true;

      const needle = String(name).toLowerCase();
      const pool = this.cache || this.items || [];

      return pool.some((clinic) => {
        const candidate = String(clinic.name || "").toLowerCase();
        if (!candidate) return false;
        /* Either direction: the API may store a tidied-up version of what was
           typed, or the customer may have typed a longer form of it. */
        return candidate.indexOf(needle) !== -1 || needle.indexOf(candidate) !== -1;
      });
    }

    /* ------------------------------------------------- registering a clinic */

    /* The whole point of the dialog: a customer who cannot find their clinic
       registers it without leaving the drawer. Sending them to the full B2B
       form meant leaving the purchase flow, and a cart abandoned on the way to
       a contact form does not usually come back. */
    openModal() {
      if (!this.modal) return;

      this.hideModalError();
      NEW_CLINIC_FIELDS.forEach((key) => this.markField(key, true));

      /* Whatever they typed in the search was their best attempt at the clinic
         name, so it is the obvious starting value. */
      const typed = this.input ? this.input.value.trim() : "";
      if (typed && this.fields.name && !this.fields.name.value) {
        this.fields.name.value = typed;
      }

      this.closeList();

      /* Moved to <body> before it is shown. The drawer slides in on a CSS
         transform, and a transformed ancestor makes position:fixed resolve
         against that ancestor rather than the viewport — so left where it is,
         the dialog would be trapped inside the drawer panel and clipped by it.
         The element keeps working from here because every reference to it and
         to its fields is held directly, not looked up through the DOM. */
      if (this.modal.parentNode !== document.body) document.body.appendChild(this.modal);

      this.modal.hidden = false;
      this.classList.add("is-adding");

      /* The first row, not the clinic name — that one is usually already filled
         in from the search, and it no longer opens the form. */
      const first = this.modal.querySelector("[data-clinic-field]");
      if (first) setTimeout(() => first.focus(), 50);
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.classList.remove("is-adding");
      this.setCreateLoading(false);
      if (this.input) this.input.focus();
    }

    /* Emptied only after a submission actually goes through. Closing without
       submitting deliberately keeps everything: a customer who dismissed the
       dialog by accident, or stepped out to check the spelling of something,
       gets their typing back when they reopen it. */
    resetModal() {
      NEW_CLINIC_FIELDS.forEach((key) => {
        if (this.fields[key]) this.fields[key].value = "";
        this.markField(key, true);
      });

      this.hideModalError();
    }

    createUrl() {
      if (/^https?:\/\//i.test(this.createPath)) return this.createPath;
      const path = this.createPath.charAt(0) === "/" ? this.createPath : `/${this.createPath}`;
      return this.apiBase ? `${this.apiBase}${path}` : "";
    }

    /* The hidden tracking the section sends, resolved the same way it resolves
       it. Empty values are dropped unless the section defines a stand-in for
       that source, so the endpoint is never sent a wall of blanks. */
    buildTracking() {
      const out = {};

      this.trackingMap.forEach((entry) => {
        out[entry.key] = trackingValue(entry.source) || TRACKING_FALLBACK;
      });

      return out;
    }

    readNewClinic() {
      const values = {};

      NEW_CLINIC_FIELDS.forEach((key) => {
        values[key] = String((this.fields[key] && this.fields[key].value) || "").trim();
      });

      return values;
    }

    /* Messages go under the field they belong to, the way the section does it,
       rather than into one summary line at the bottom. Returns true when the
       form is good. */
    validateNewClinic(values) {
      let firstBad = null;

      NEW_CLINIC_FIELDS.forEach((key) => {
        if (values[key]) {
          this.markField(key, true);
          return;
        }

        this.markField(key, false, this.text.createRequired);
        if (!firstBad) firstBad = key;
      });

      if (!firstBad) {
        const emailProblem = this.emailProblem(values.email);

        if (emailProblem) {
          this.markField("email", false, emailProblem);
          firstBad = "email";
        }
      }

      if (firstBad) this.focusField(firstBad);
      return !firstBad;
    }

    /* Returns the message for whatever is wrong with the address, or "" when it
       passes. Shared by the submit check and the blur check so the two can
       never disagree about what counts as valid. */
    emailProblem(value) {
      const email = String(value || "").trim();

      if (!email) return this.text.createRequired;
      if (!EMAIL_RE.test(email)) return this.text.createEmail;
      if (this.businessEmail && isFreeEmail(email)) return this.text.createBusinessEmail;

      return "";
    }

    markField(key, ok, message) {
      const field = this.fields[key];
      if (!field) return;

      const row = field.closest("[data-clinic-field-row]");
      if (row) {
        row.classList.toggle("is-invalid", !ok);

        const slot = row.querySelector("[data-clinic-field-error]");
        if (slot) slot.textContent = ok ? "" : message || "";
      }

      field.setAttribute("aria-invalid", ok ? "false" : "true");
    }

    focusField(key) {
      const field = this.fields[key];
      if (field) field.focus();
    }

    createClinic() {
      if (this.busy) return;

      const values = this.readNewClinic();

      if (!this.validateNewClinic(values)) {
        /* Each bad field is already carrying its own message. */
        this.hideModalError();
        return;
      }

      const endpoint = this.createUrl();

      if (!endpoint) {
        this.showModalError(this.text.createError);
        /* eslint-disable-next-line no-console */
        console.error("[clinic-selector] no submit endpoint — set the API base URL in theme settings");
        return;
      }

      /* Tracking first, so a mis-mapped tracking key can never overwrite a real
         field the customer typed. */
      const payload = this.buildTracking();
      NEW_CLINIC_FIELDS.forEach((key) => {
        payload[this.apiKeys[key]] = values[key];
      });

      const body = this.createWrapKey ? { [this.createWrapKey]: payload } : payload;
      const headers = Object.assign(
        { "Content-Type": "application/json", Accept: "application/json" },
        this.createHeaders,
      );

      this.busy = true;
      this.setCreateLoading(true);
      this.hideModalError();

      fetch(endpoint, { method: "POST", headers, body: JSON.stringify(body) })
        .then((response) =>
          response.text().then((text) => {
            /* The body is parsed whatever the status. A 409 carries the reason
               the submission was refused — "a clinic with this email already
               exists" — and throwing it away here is what left the customer
               staring at a generic apology with no idea what to change. */
            let data = {};

            try {
              data = text ? JSON.parse(text) : {};
            } catch (error) {
              data = {};
            }

            return { ok: response.ok, status: response.status, data };
          }),
        )
        .then(({ ok, status, data }) => {
          /* Two ways this can be a refusal: an error status, or a 200 carrying
             an error body — plenty of endpoints answer a duplicate that way.
             Either must keep the dialog open with the fields intact, because
             the customer has something to correct. Nothing below this point
             runs unless the submission really landed. */
          const problem = ok ? this.apiProblem(data) : this.refusalMessage(data);

          if (problem) {
            this.busy = false;
            this.setCreateLoading(false);

            /* Named fields first, then the wording of the message — the
               endpoint says which value clashed even when it does not say
               which field. */
            this.markApiFieldErrors(data && data.errors);
            this.markConflictField(problem);
            this.showModalError(problem);

            /* eslint-disable-next-line no-console */
            console.warn(`[clinic-selector] the endpoint rejected the submission (HTTP ${status})`, data);
            return null;
          }

          const clinic = this.clinicFromResponse(data, values.name);

          if (this.debug) {
            /* eslint-disable-next-line no-console */
            console.log("[clinic-selector] clinic submitted", { sent: payload, got: data, using: clinic });
          }

          /* Deliberately not awaited. Sales getting the lead matters, but not
             enough to hold up a checkout if the store's contact form is slow. */
          this.notifyByEmail(values, payload);

          /* The list on file no longer includes every clinic. */
          this.cache = null;

          this.busy = false;
          this.setCreateLoading(false);

          /* Cleared on success so the dialog reopens blank. Someone who got a
             detail wrong the first time can register the clinic again without
             having to delete the previous attempt field by field. */
          this.resetModal();
          this.closeModal();

          if (clinic) {
            /* choose() writes the properties to every matching line item and
               refreshes the drawer, so the customer lands back on a cart that
               is ready to check out. The note is parked rather than shown,
               because that refresh may well replace this element first. */
            pendingNotice = this.text.createSuccess;
            return this.choose(clinic).then(() => this.flushNotice());
          }

          /* The usual path with a lead endpoint: it accepted the submission but
             does not hand back a clinic record. Search for it instead, with the
             retries that cover an index that has not caught up. */
          this.refetchAfterSubmit(values.name, 0);
          return null;
        })
        .catch((error) => {
          this.busy = false;
          this.setCreateLoading(false);
          this.showModalError(this.text.createError);
          /* eslint-disable-next-line no-console */
          console.error("[clinic-selector] could not create the clinic", error, endpoint);
        });
    }

    /* Returns the message to show when the body says the submission failed, or
       "" when it looks fine. Only shapes that are unambiguously a failure count
       — an absent key, a null error and an empty errors list all mean success,
       because the cost of a false positive here is blocking a customer who
       actually got through. */
    apiProblem(data) {
      if (!data || typeof data !== "object") return "";

      const errors = data.errors;
      const errorCount = errors ? (Array.isArray(errors) ? errors.length : Object.keys(errors).length) : 0;

      const failed =
        data.success === false ||
        data.ok === false ||
        data.status === "error" ||
        data.status === "fail" ||
        Boolean(data.error) ||
        errorCount > 0;

      if (!failed) return "";

      return firstMessage(data.message) || firstMessage(data.error) || firstMessage(errors) || this.text.createError;
    }

    /* The message from an error status. "error" is skipped deliberately: on a
       409 it holds the status name ("Conflict"), which tells the customer
       nothing. */
    refusalMessage(data) {
      return firstMessage(data && data.message) || firstMessage(data && data.errors) || this.text.createError;
    }

    /* The endpoint says which value clashed but not which input. Reading it off
       the wording is all that is available, and it is worth doing: a duplicate
       is fixed by editing one specific field, and the customer should not have
       to guess which. */
    markConflictField(message) {
      const text = String(message || "").toLowerCase();

      if (text.indexOf("email") !== -1) {
        this.markField("email", false, message);
        this.focusField("email");
        return;
      }

      /* Checked after email, and never the contact's own name — a clinic-name
         conflict is about the clinic. */
      if (text.indexOf("name") !== -1 || text.indexOf("clinic") !== -1) {
        this.markField("name", false, message);
        this.focusField("name");
      }
    }

    /* When the endpoint names the fields it objected to, put its message under
       the field it belongs to rather than only in the summary line — the
       customer can then see which one to fix. Keys are matched against what was
       actually sent, so this follows the payload key settings automatically. */
    markApiFieldErrors(errors) {
      if (!errors || typeof errors !== "object" || Array.isArray(errors)) return;

      const byApiKey = {};
      NEW_CLINIC_FIELDS.forEach((key) => {
        byApiKey[this.apiKeys[key]] = key;
      });

      Object.keys(errors).forEach((apiKey) => {
        const key = byApiKey[apiKey];
        if (!key) return;
        this.markField(key, false, firstMessage(errors[apiKey]) || this.text.createError);
      });
    }

    /* A lead endpoint usually answers with an acknowledgement rather than a
       record, so this returning null is the normal case, not a failure. When it
       does return something with an id, that is worth taking: the clinic can be
       selected on the spot instead of waiting for the search index. */
    clinicFromResponse(data, fallbackName) {
      if (!data) return null;

      let candidate = data.clinic || data.data || data.result || data;
      if (Array.isArray(candidate)) candidate = candidate[0];

      if (!candidate || !candidate.id) return null;

      return {
        id: candidate.id,
        name: candidate.name || fallbackName,
        city: candidate.city,
      };
    }

    /* Mirrors what the B2B form does on submit, so a clinic registered from the
       drawer reaches the store inbox the same way one registered from the full
       form does. */
    notifyByEmail(values, payload) {
      if (!this.sendEmail) return;

      const params = new URLSearchParams();
      params.append("form_type", "contact");
      params.append("utf8", "✓");
      params.append("contact[email]", values.email);
      params.append("contact[name]", `${values.firstName} ${values.lastName}`.trim());
      params.append("contact[phone]", values.phone);

      /* Built from the payload rather than the raw values, so the inbox copy is
         labelled exactly as the API received it, tracking included — otherwise
         the notification would say less about the lead than the section's does. */
      params.append(
        "contact[body]",
        Object.keys(payload)
          .map((key) => `${key}: ${payload[key]}`)
          .join("\n"),
      );

      fetch("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: params,
      }).catch((error) => {
        /* eslint-disable-next-line no-console */
        console.warn("[clinic-selector] email notification failed", error);
      });
    }

    /* is-loading on the .b2bcf__form wrapper is what the section's stylesheet
       keys off: it swaps the button label for the spinner already inside it. */
    setCreateLoading(on) {
      if (this.createButton) this.createButton.disabled = !!on;
      if (this.createForm) this.createForm.classList.toggle("is-loading", !!on);
    }

    showModalError(text) {
      if (!this.modalError) return;
      this.modalError.textContent = text;
      this.modalError.hidden = false;
    }

    hideModalError() {
      if (!this.modalError) return;
      this.modalError.hidden = true;
      this.modalError.textContent = "";
    }

    /* ----------------------------------------------------------- cart writes */

    getCart() {
      return fetch(`${root()}/cart.js`, { headers: { Accept: "application/json" } }).then((response) =>
        response.json(),
      );
    }

    matches(item) {
      return this.productIds.indexOf(String(item.product_id)) !== -1;
    }

    /* One line per pass, re-reading the cart each time: changing properties
       issues a new line key, so a list gathered up front goes stale. */
    writeToCart(clinic) {
      const label = this.labelFor(clinic);
      let writes = 0;

      const step = () => {
        if (writes++ > MAX_CART_WRITES) {
          return Promise.reject(new Error("too many cart writes — aborting"));
        }

        return this.getCart().then((cart) => {
          const target = (cart.items || []).find((item) => {
            const props = item.properties || {};
            return (
              this.matches(item) && (props[this.propId] !== clinic.id || props[this.propName] !== label)
            );
          });

          if (!target) return cart;

          const properties = Object.assign({}, target.properties || {});
          properties[this.propId] = clinic.id;
          properties[this.propName] = label;

          return fetch(`${root()}/cart/change.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: target.key, quantity: target.quantity, properties }),
          })
            .then((response) => {
              if (!response.ok) {
                return response.text().then((body) => {
                  throw new Error(`cart/change.js ${response.status} — ${body.slice(0, 200)}`);
                });
              }
              return response.json();
            })
            .then(step);
        });
      };

      return step();
    }

    /* Returns a promise so a caller that created the clinic can wait for it to
       be saved before saying so. */
    choose(clinic) {
      if (!clinic || this.busy) return Promise.resolve();

      this.busy = true;
      this.closeList();
      this.setSpinner(true);
      this.hideMessage();

      return this.writeToCart(clinic)
        .then(() => {
          this.selected = { id: clinic.id, label: this.labelFor(clinic) };
          this.showChosen();
          this.updateCheckoutState();
          return this.refreshDrawer();
        })
        .catch((error) => {
          this.showMessage(this.text.error);
          /* eslint-disable-next-line no-console */
          console.error("[clinic-selector] could not save the clinic", error);
        })
        .then(() => {
          this.busy = false;
          this.setSpinner(false);
        });
    }

    /* Only the two regions that actually changed are replaced — swapping the
       whole section would destroy this element and close the drawer. */
    refreshDrawer() {
      return fetch(`${cartUrl()}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, "text/html");

          ["cart-drawer-items", ".cart-drawer__footer"].forEach((selector) => {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          });
        })
        .catch((error) => {
          /* Not fatal — the properties are already saved. */
          console.warn("[clinic-selector] drawer refresh failed", error);
        });
    }

    /* ----------------------------------------------------------------- state */

    /* Keeps the selector honest when the cart changes underneath it: hides
       itself once the last matching product is gone, and pushes an already
       chosen clinic onto any matching product added afterwards. */
    syncFromCart() {
      if (this.busy) return Promise.resolve();

      return this.getCart()
        .then((cart) => {
          const matching = (cart.items || []).filter((item) => this.matches(item));

          this.hidden = matching.length === 0;

          if (!matching.length) {
            this.updateCheckoutState();
            return null;
          }

          const carrier = matching.find((item) => (item.properties || {})[this.propId]);

          if (!carrier) {
            this.selected = null;
            this.showSearch();
            this.updateCheckoutState();
            return null;
          }

          const props = carrier.properties || {};
          this.selected = { id: props[this.propId], label: props[this.propName] || "" };
          this.showChosen();
          this.updateCheckoutState();

          const stale = matching.filter((item) => (item.properties || {})[this.propId] !== this.selected.id);
          if (!stale.length) return null;

          this.busy = true;
          return this.writeToCart({ id: this.selected.id, name: this.selected.label })
            .then(() => this.refreshDrawer())
            .catch((error) => console.warn("[clinic-selector] could not propagate the clinic", error))
            .then(() => {
              this.busy = false;
            });
        })
        .catch((error) => console.warn("[clinic-selector] could not read the cart", error));
    }

    watchCart() {
      const host = this.closest("#CartDrawer") || document.querySelector("cart-drawer");
      if (!host || typeof MutationObserver !== "function") return;

      this.observer = new MutationObserver((records) => {
        /* Ignore mutations we caused inside this element. */
        const external = records.some((record) => !this.contains(record.target));
        if (!external || this.busy) return;

        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.syncFromCart(), SYNC_DEBOUNCE_MS);
      });

      this.observer.observe(host, { childList: true, subtree: true });
    }

    showChosen() {
      if (!this.selected) return;
      if (this.chosenName) this.chosenName.textContent = this.selected.label || this.selected.id;
      if (this.chosenPane) this.chosenPane.hidden = false;
      if (this.changeButton) this.changeButton.hidden = false;
      if (this.searchPane) this.searchPane.hidden = true;
      this.classList.add("is-selected");
      this.hideMessage();
    }

    showSearch() {
      /* Changing swaps the two panes outright — the saved clinic is hidden and
         only the search field is on screen. The clinic itself is untouched: it
         stays on the line items until a different one replaces it, so cancelling
         by selecting nothing leaves the order exactly as it was, and the chosen
         pane comes straight back on the next showChosen() or cart sync. */
      /* Only a real switch into search mode resets the field. Without this the
         customer loses what they are typing on every cart change: a customer
         with no clinic yet takes this path on each syncFromCart, and the cart
         mutating for any reason would empty the box under them. */
      const entering = this.searchPane ? this.searchPane.hidden : true;

      if (this.chosenPane) this.chosenPane.hidden = true;
      if (this.changeButton) this.changeButton.hidden = false;
      if (this.searchPane) this.searchPane.hidden = false;
      this.classList.remove("is-selected");

      if (this.input && entering) {
        this.input.value = "";
        this.input.focus();
      }

      const current = this.input ? this.input.value.trim() : "";

      if (this.inline) {
        /* Keep the list on screen; re-run whatever filter is in the box. */
        this.search(current);
      } else if (entering) {
        this.items = [];
        this.closeList();
      }

      this.updateCheckoutState();
    }

    updateCheckoutState() {
      if (!this.required) return;

      const blocked = !this.hidden && !this.selected;
      const buttons = document.querySelectorAll(
        '[name="checkout"], #CartDrawer-Checkout, .cart__checkout-button',
      );

      buttons.forEach((button) => {
        button.disabled = blocked;
        button.classList.toggle("clinic-selector__blocked", blocked);
      });

      if (blocked) {
        this.showMessage(this.text.required, "note");
      } else {
        this.hideMessage();
      }
    }

    setSpinner(on) {
      if (this.spinner) this.spinner.hidden = !on;
      this.classList.toggle("is-loading", !!on);
    }

    showMessage(text, kind) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.hidden = false;
      this.message.classList.toggle("clinic-selector__message--note", kind === "note");
    }

    hideMessage() {
      if (!this.message) return;
      this.message.hidden = true;
      this.message.textContent = "";
    }

    /* The isConnected guard is what makes the handover work: the element that
       started the save is detached by then, and showing the note on it would
       clear the flag without anyone seeing it. */
    flushNotice() {
      if (!pendingNotice || !this.isConnected) return;
      const notice = pendingNotice;
      pendingNotice = "";
      this.showMessage(notice, "note");
    }
  }

  customElements.define("clinic-selector", ClinicSelector);
})();